import { Component, EventEmitter, Input, OnInit, Output, Renderer2 } from '@angular/core';
import { of, PartialObserver } from 'rxjs';
import { GenericFormGroupComponent } from '../../../../../forms/generic-form-group.component';
import { GradingScheduleService } from '../../../../../shared/grading-schedule.service';
import { GradingScheduleList, NewGradingSchedule } from '../../../../../shared/interface/grading-schedule.interface';
import { ScheduleTerm, ScheduleTermList } from '../../../../../shared/interface/term-interface';
import { AlertService } from '../../../../../shared/services/alert.service';
import { TermService } from '../../../../../shared/term.service';
import { FormControl, Validators } from '@angular/forms';
import { QuestionDefinition, QuestionValueChange, SelectInputType } from '../../../../../forms/form.types';
import { DialogDataService } from '../../../../../dialog/dialog-data.service';
import { DialogService } from '../../../../../dialog/dialog.service';
import { nameof } from 'ts-simple-nameof';
import { Observable } from 'rxjs/Observable';

@Component({
    selector: 'app-registrar-admin-maintenance-schedule-form',
    templateUrl: './schedule-form.component.html',
    styleUrls: ['./schedule-form.component.scss']
})
export class ScheduleFormComponent extends GenericFormGroupComponent implements OnInit {
    controls: {
        id: FormControl<number>;
        termCode: FormControl<string>;
        termStartDate: FormControl<Date>;
        openDate: FormControl<Date>;
        enabled: FormControl<boolean>;
        numberTimeAllowedGradeChange: FormControl<number>;
    };

    questions: {
        id: QuestionDefinition;
        termCode: QuestionDefinition;
        termStartDate: QuestionDefinition;
        openDate: QuestionDefinition;
        numberTimeAllowedGradeChange: QuestionDefinition;
        enabled: QuestionDefinition;
    };

    constructor(
        public data: DialogDataService<GradingScheduleList>,
        public dialog: DialogService<GradingScheduleList>,
        public termService: TermService,
        public gradingScheduleService: GradingScheduleService,
        public alertService: AlertService,
        protected renderer2: Renderer2
    ) {
        super(renderer2);
        this.getTermDropDown();
    }

    addEditText: String;
    saveUpdateText: String;

    termList: Array<ScheduleTerm>;
    private options: Array<ScheduleTerm> = [];
    initialTermList: Array<ScheduleTerm>;

    @Input() currentDataSentForEdit: GradingScheduleList;
    @Input() newCurrentDataSent: boolean;
    @Input() formTermHasBeenLoaded: boolean;
    @Output() updateScheduleList = new EventEmitter<Array<GradingScheduleList>>();
    @Output() formTermHasBeenLoadedChange = new EventEmitter<boolean>();

    ngOnInit(): void {
        this.addEditText = 'Add';
        this.saveUpdateText = 'Save';
        this.initControlsAndQuestions();
    }

    ngAfterViewInit(): void {}

    getTermDropDown(): void {
        this.termService.getNonScheduleTerm().subscribe((response: ScheduleTermList) => {
            if (response) {
                this.termList = response.list;
                this.initialTermList = response.list;
                this.formTermHasBeenLoadedChange.emit(true);
                (this.questions.termCode.input as SelectInputType).updateOptions = true;
            }
        });
    }

    private initControlsAndQuestions() {
        this.controls = {
            id: new FormControl<number>(this.data.data?.id),
            termCode: new FormControl<string>(this.data.data?.termCode, Validators.required),
            termStartDate: new FormControl<Date>(this.data.data?.termStartDate, Validators.required),
            openDate: new FormControl<Date>(this.data.data?.openDate, Validators.required),
            numberTimeAllowedGradeChange: new FormControl<number>(
                this.data.data?.numberTimeAllowedGradeChange,
                Validators.required
            ),
            enabled: new FormControl<boolean>(this.data.data?.enabled ?? false)
        };
        this.createFormGroup(this.controls);

        this.questions = {
            id: {
                input: {
                    discriminator: 'TEXT',
                    property: nameof<GradingScheduleList>((m) => m.id),
                    control: this.controls.id,
                    required: false
                }
            },
            termCode: {
                input: {
                    discriminator: 'SELECT',
                    property: 'termCode',
                    label: 'Select a Term',
                    control: this.controls.termCode,
                    optionsCallBack: {
                        this: this,
                        function: this.renderOptions
                    },
                    required: true
                }
            },
            termStartDate: {
                input: {
                    discriminator: 'DATE',
                    property: nameof<GradingScheduleList>((m) => m.termStartDate),
                    control: this.controls.termStartDate,
                    required: true
                }
            },
            openDate: {
                input: {
                    discriminator: 'DATE-TIME',
                    property: nameof<GradingScheduleList>((m) => m.openDate),
                    control: this.controls.openDate,
                    required: true
                }
            },
            numberTimeAllowedGradeChange: {
                input: {
                    discriminator: 'TEXT',
                    label: 'Grade Change Limit',
                    property: nameof<GradingScheduleList>((m) => m.numberTimeAllowedGradeChange),
                    control: this.controls.numberTimeAllowedGradeChange,
                    required: true
                }
            },
            enabled: {
                input: {
                    label: 'Grading Open',
                    discriminator: 'CHECKBOX',
                    property: nameof<GradingScheduleList>((m) => m.enabled),
                    control: this.controls.enabled
                }
            }
        };
        this.questions?.enabled?.input?.control?.setValue(true); // set defautl true
    }

    private renderOptions(): Observable<Array<ScheduleTerm>> {
        if (this.termList?.length) {
            this.options = this.termList?.map((m) => {
                return {
                    text: m.text,
                    value: m.value
                } as ScheduleTerm;
            });
        }
        return of(this.options);
    }

    refreshSchedule(updateScheduleList: Array<GradingScheduleList>): void {
        this.updateScheduleList.emit(updateScheduleList);
        this.resetForm();
    }

    resetForm(): void {
        this.clearAllValuesAndMarkPristine();
        this.getTermDropDown();
        this.addEditText = 'Add';
        this.saveUpdateText = 'Save';
        (this.questions.termCode.input as SelectInputType).updateOptions = true;
    }

    validateForm() {
        let isFormValid: boolean = false;
        const currentFormValue = this.getFormValue<NewGradingSchedule>();

        const errorMessage = [];
        if (!currentFormValue.termCode) {
            errorMessage.push('The term is required.');
        }
        if (currentFormValue.termStartDate > currentFormValue.openDate) {
            errorMessage.push('The term start date cannot be before the open date or close date.');
        }

        if (isNaN(currentFormValue.numberTimeAllowedGradeChange)) {
            errorMessage.push('The number time allow grade change needs to be a number');
        } else {
            if (currentFormValue.numberTimeAllowedGradeChange <= 0) {
                errorMessage.push('The number time allow grade change needs to be 1 or more');
            }
        }

        if (errorMessage.length == 0) {
            isFormValid = true;
        } else {
            isFormValid = false;
        }

        return { isFormValid: isFormValid, errorMessage: errorMessage };
    }

    private gradingScheduleObserver: PartialObserver<Array<GradingScheduleList>> = {
        next: (response: Array<GradingScheduleList>) => {
            this.setDoneSaving();
            if (this.addEditText === 'Add') {
                this.alertService.toastSuccess('Schedule added.');
            } else {
                this.alertService.toastSuccess('Schedule updated.');
            }
            this.refreshSchedule(response);
        },
        error: (err) => {
            this.setDoneSaving();
            console.log(err);
            this.alertService.toastErrorObject(err);
        }
    };

    saveUpdateData() {
        const validatedForm = this.validateForm();
        console.log(this.getFormValue());
        console.log(this.getFormValue<NewGradingSchedule>());

        if (validatedForm.isFormValid) {
            this.setSaving();
            if (this.addEditText == 'Add') {
                this.gradingScheduleService
                    .post(this.getFormValue<NewGradingSchedule>())
                    .subscribe(this.gradingScheduleObserver);
            } else {
                this.gradingScheduleService
                    .put(this.getFormValue<NewGradingSchedule>())
                    .subscribe(this.gradingScheduleObserver);
            }
        } else {
            //alert error message
            const alertMsg: string =
                '<div class="grad-validate-error-msg-size">' + validatedForm.errorMessage.join('<br>') + '</div>';

            this.alertService.error({
                text: alertMsg
            });
        }
        this.resetForm();
    }

    updateSelectATerm($event: QuestionValueChange) {
        const selectTerm: ScheduleTerm = this.termList.find((item: ScheduleTerm) => {
            return item.value == $event.payload;
        });

        this.questions?.termStartDate?.input?.control?.setValue(selectTerm?.termOpenDateForIGrade);
    }

    updateCurrenFormEdit(updateData: GradingScheduleList) {
        this.clearAllValuesAndMarkPristine();
        const scheduleEdit: ScheduleTerm = {
            text: updateData.termDescription,
            value: updateData.termCode,
            termOpenDate: updateData.termStartDate,
            termOpenDateForIGrade: updateData.openDate
        };

        this.controls?.id?.setValue(updateData.id);
        this.controls?.termCode?.setValue(updateData.termCode);
        (this.questions.termCode.input as SelectInputType).updateOptions = true;
        this.controls?.termStartDate?.setValue(updateData.termStartDate);
        this.controls?.openDate?.setValue(new Date(updateData.openDate));
        this.controls?.numberTimeAllowedGradeChange?.setValue(updateData.numberTimeAllowedGradeChange);
        this.controls?.enabled?.setValue(updateData.enabled);

        this.addEditText = 'Edit';
        this.saveUpdateText = 'Update';
        this.termList = [scheduleEdit];
    }
    ngOnChanges(): void {
        // to-do:  if hit cancle and click on the button this does not get trigered
    }

    onShowSaveButton(): boolean {
        const validForm = this.validateForm();
        return !!validForm?.isFormValid;
    }

    onCancel(): void {
        this.setSaving();
        this.resetForm();
        this.setDoneSaving();
    }
}
